import { useEffect, useRef, useState } from "react"
import {
  X,
  Trash2,
  Loader2,
  AlertTriangle,
  Zap,
  Edit2,
  Users,
  ChevronDown,
  Plus,
} from "lucide-react"

// ─── Design tokens (matches InterviewsTab) ────────────────────────────────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border:       "0.5px solid #e5e7eb",
  borderHover:  "0.5px solid #d1d5db",
  radius:       8,
  radiusCard:   10,
  bg:           "#fff",
  bgPage:       "#f5f5f0",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const sanitizeHTML = (html) => {
  if (!html) return ""
  const div = document.createElement("div")
  div.innerHTML = html
  Array.from(div.getElementsByTagName("script")).forEach((s) => s.remove())
  Array.from(div.getElementsByTagName("*")).forEach((el) => {
    ;["onclick", "onload", "onerror", "color", "style"].forEach((a) =>
      el.removeAttribute(a),
    )
    if (el.tagName.toLowerCase() === "font") {
      const span = document.createElement("span")
      span.innerHTML = el.innerHTML
      el.parentNode.replaceChild(span, el)
    }
  })
  return div.innerHTML
}

const formatSalary = (value, curr) => {
  if (!value) return null
  const num = parseInt(value, 10)
  if (isNaN(num)) return null
  if (curr === "INR") {
    const s = num.toString()
    const last3 = s.slice(-3)
    const rest = s.slice(0, -3)
    return (
      "₹" +
      (rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3 : last3)
    )
  }
  return (
    ({ USD: "$", EUR: "€", GBP: "£" }[curr] || curr + " ") +
    num.toLocaleString()
  )
}

// ─── Avatar (square, matches InterviewsTab sidebar avatars) ──────────────────
const AVATAR_COLORS = [
  { bg: "#dbeafe", text: "#1d4ed8" },
  { bg: "#dcfce7", text: "#15803d" },
  { bg: "#ede9fe", text: "#7c3aed" },
  { bg: "#fef3c7", text: "#b45309" },
  { bg: "#fce7f3", text: "#be185d" },
  { bg: "#e0f2fe", text: "#0369a1" },
]

const Avatar = ({ name, size = 26 }) => {
  const initials = name
    ? name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?"
  const color = AVATAR_COLORS[initials.charCodeAt(0) % AVATAR_COLORS.length]
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        background: color.bg,
        color: color.text,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.38,
        fontWeight: 700,
        flexShrink: 0,
        letterSpacing: "0.02em",
        fontFamily: MONO,
      }}
    >
      {initials}
    </div>
  )
}

// ─── Status pill ──────────────────────────────────────────────────────────────
const STATUS = {
  active:  { label: "Active",   bg: "#dcfce7", color: "#166534" },
  draft:   { label: "Draft",    bg: "#f3f4f6", color: "#6b7280" },
  closed:  { label: "Closed",   bg: "#f3f4f6", color: "#9ca3af" },
  on_hold: { label: "On Hold",  bg: "#fef3c7", color: "#92400e" },
}

const StatusPill = ({ status }) => {
  const s = STATUS[status] ?? STATUS.draft
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 700,
        background: s.bg,
        color: s.color,
        letterSpacing: "0.03em",
        fontFamily: MONO,
      }}
    >
      {s.label}
    </span>
  )
}

// ─── Section divider label (matches InterviewsTab column header style) ────────
const SectionLabel = ({ children }) => (
  <p
    style={{
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: T.textTertiary,
      margin: "0 0 12px",
      fontFamily: MONO,
    }}
  >
    {children}
  </p>
)

// ─── Sidebar key/value ────────────────────────────────────────────────────────
const SbKey = ({ children }) => (
  <p
    style={{
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: "#8a9a8a",
      margin: "0 0 2px",
      fontFamily: MONO,
    }}
  >
    {children}
  </p>
)

const SbVal = ({ children }) => (
  <p
    style={{
      fontSize: 13,
      fontWeight: 500,
      color: T.text,
      margin: 0,
      fontFamily: FF,
    }}
  >
    {children}
  </p>
)

// ─── Sidebar divider ──────────────────────────────────────────────────────────
const SbDivider = () => (
  <div
    style={{
      height: 1,
      background: "#e5e7eb",
      margin: "16px 0",
    }}
  />
)

// ─── Person row (sidebar) ─────────────────────────────────────────────────────
const PersonRow = ({ name, sub }) => {
  if (!name) return null
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}
    >
      <Avatar name={name} size={26} />
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: T.text,
            margin: 0,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: FF,
          }}
        >
          {name}
        </p>
        {sub && (
          <p
            style={{
              fontSize: 11,
              color: T.textTertiary,
              margin: 0,
              lineHeight: 1.3,
              fontFamily: FF,
            }}
          >
            {sub}
          </p>
        )}
      </div>
    </div>
  )
}

// ─── JD body with 25-line clip ────────────────────────────────────────────────
const JdBody = ({ html }) => {
  const [expanded, setExpanded] = useState(false)
  const LINE_HEIGHT = 1.75
  const FONT_SIZE = 13.5
  const MAX_LINES = 25
  const maxHeight = MAX_LINES * LINE_HEIGHT * FONT_SIZE

  return (
    <div>
      <div
        className="jdp-prose"
        style={{
          maxHeight: expanded ? "none" : maxHeight,
          overflow: "hidden",
          position: "relative",
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(html) }}
      />
      {!expanded && (
        <div
          style={{
            marginTop: -32,
            paddingTop: 32,
            background: "linear-gradient(to bottom, transparent, #fff 70%)",
            position: "relative",
          }}
        >
          <button
            onClick={() => setExpanded(true)}
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#2563eb",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "block",
              marginTop: 6,
              fontFamily: MONO,
              letterSpacing: "0.04em",
            }}
          >
            Show full description ↓
          </button>
        </div>
      )}
      {expanded && (
        <button
          onClick={() => setExpanded(false)}
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: T.textTertiary,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px 0 0",
            display: "block",
            fontFamily: MONO,
            letterSpacing: "0.04em",
          }}
        >
          Show less ↑
        </button>
      )}
    </div>
  )
}

// ─── Delete confirm sheet ─────────────────────────────────────────────────────
const DeleteSheet = ({ jobTitle, onConfirm, onCancel, isDeleting }) => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      zIndex: 10,
      display: "flex",
      alignItems: "flex-end",
      background: "rgba(0,0,0,0.08)",
    }}
  >
    <div
      style={{
        width: "100%",
        background: "#fff",
        borderTop: T.border,
        padding: "20px 24px",
      }}
    >
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "#fef2f2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AlertTriangle size={14} color="#ef4444" />
        </div>
        <div>
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: T.text,
              margin: "0 0 4px",
              fontFamily: FF,
            }}
          >
            Delete this job posting?
          </p>
          <p style={{ fontSize: 13, color: T.textTertiary, margin: 0, fontFamily: FF }}>
            <span style={{ color: T.textMuted }}>"{jobTitle}"</span> will be
            permanently removed.
          </p>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, paddingLeft: 44 }}>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 30,
            padding: "0 14px",
            borderRadius: T.radius - 1,
            fontSize: 12,
            fontWeight: 700,
            background: "#dc2626",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            opacity: isDeleting ? 0.6 : 1,
            fontFamily: MONO,
            letterSpacing: "0.04em",
          }}
        >
          {isDeleting && <Loader2 size={12} className="animate-spin" />}
          {isDeleting ? "Deleting…" : "Delete"}
        </button>
        <button
          onClick={onCancel}
          disabled={isDeleting}
          style={{
            height: 30,
            padding: "0 14px",
            borderRadius: T.radius - 1,
            fontSize: 12,
            fontWeight: 700,
            background: "none",
            color: T.textTertiary,
            border: T.border,
            cursor: "pointer",
            fontFamily: MONO,
            letterSpacing: "0.04em",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)

// ─── Main panel ───────────────────────────────────────────────────────────────
const JobDetailsPanel = ({
  listing,
  organization,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  isDeleting,
  navigate,
}) => {
  const backdropRef = useRef(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    const h = (e) => {
      if (e.target === backdropRef.current) onClose()
    }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [onClose])

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", h)
    return () => document.removeEventListener("keydown", h)
  }, [onClose])

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) setConfirmDelete(false)
  }, [isOpen])

  if (!listing) return null

  const cur = listing?.salaryRange?.currency || "INR"
  const sMin = formatSalary(listing?.salaryRange?.min, cur)
  const sMax = formatSalary(listing?.salaryRange?.max, cur)
  const salary = sMin && sMax ? `${sMin} – ${sMax}` : sMin || sMax || null

  const exp = listing?.candidateProfile?.experience
  const expDB = listing?.candidateProfile?.experienceDealbreaker
  const notice = listing?.candidateProfile?.noticePeriod
  const noticeDB = listing?.candidateProfile?.noticePeriodDealbreaker
  const ideal = Array.isArray(listing?.candidateProfile?.idealCandidateProfile)
    ? listing.candidateProfile.idealCandidateProfile
    : []
  const flags = Array.isArray(listing?.candidateProfile?.redFlags)
    ? listing.candidateProfile.redFlags
    : []
  const qs = Array.isArray(listing?.applicationForm?.questions)
    ? listing.applicationForm.questions
    : []
  const rounds = Array.isArray(listing?.interviewStructure?.rounds)
    ? listing.interviewStructure.rounds
    : []

  const editFn = () => {
    onEdit?.(listing)
    onClose()
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
        .jdp-root * { font-family: 'DM Sans', -apple-system, sans-serif; box-sizing: border-box; }
        .jdp-left::-webkit-scrollbar { display: none; }
        .jdp-left { scrollbar-width: none; }
        .jdp-right::-webkit-scrollbar { display: none; }
        .jdp-right { scrollbar-width: none; }
        .jdp-prose { font-size: 13.5px; line-height: 1.75; color: #374151; }
        .jdp-prose p { margin-bottom: .85rem; }
        .jdp-prose ul, .jdp-prose ol { margin: 0 0 .85rem 1.25rem; }
        .jdp-prose li { margin-bottom: .3rem; }
        .jdp-prose h2, .jdp-prose h3 { font-size: 13.5px; font-weight: 600; color: #111827; margin: 1rem 0 .4rem; }
        .jdp-prose strong { font-weight: 600; color: #1f2937; }
        .jdp-round-row:hover { background: #fafaf9 !important; }
        .jdp-icon-btn:hover { background: #111827 !important; color: #fff !important; border-color: #111827 !important; }
        .jdp-ghost-btn:hover { background: #f9fafb !important; }
        .jdp-delete-btn:hover { background: #fef2f2 !important; }
        .jdp-topbar-btn:hover { background: #f9fafb !important; }
      `}</style>

      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="jdp-root"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 50,
          background: isOpen ? "rgba(0,0,0,0.18)" : "transparent",
          pointerEvents: isOpen ? "auto" : "none",
          transition: "background 200ms",
        }}
      >
        {/* Drawer */}
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "82vw",
            maxWidth: 1080,
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            transform: isOpen ? "translateX(0)" : "translateX(100%)",
            transition: "transform 300ms cubic-bezier(0.32, 0.72, 0, 1)",
            boxShadow: isOpen ? "-8px 0 40px rgba(0,0,0,0.10)" : "none",
          }}
        >
          {confirmDelete && (
            <DeleteSheet
              jobTitle={listing.role}
              onConfirm={async () => {
                await onDelete(listing.id)
                setConfirmDelete(false)
                onClose()
              }}
              onCancel={() => setConfirmDelete(false)}
              isDeleting={isDeleting}
            />
          )}

          {/* ── HEADER ─────────────────────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "13px 20px",
              borderBottom: T.border,
              background: T.bg,
              flexShrink: 0,
            }}
          >
            {/* Dot — matches InterviewsTab topbar */}
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: T.text,
                flexShrink: 0,
              }}
            />

            {/* Title block */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {listing?.department && (
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: T.textTertiary,
                    margin: "0 0 2px",
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    fontFamily: MONO,
                  }}
                >
                  {listing.department}
                </p>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h2
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: T.text,
                    margin: 0,
                    lineHeight: 1.2,
                    letterSpacing: "-0.01em",
                    fontFamily: FF,
                  }}
                >
                  {listing?.role}
                </h2>
                <StatusPill status={listing?.status ?? "active"} />
              </div>
            </div>

            {/* Action buttons — matches InterviewsTab ActionBtn style */}
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              <button
                className="jdp-topbar-btn"
                onClick={editFn}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  height: 30,
                  padding: "0 10px",
                  borderRadius: T.radius - 1,
                  border: T.border,
                  background: T.bg,
                  color: T.textMuted,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: MONO,
                  letterSpacing: "0.04em",
                  outline: "none",
                  transition: "background 0.12s",
                }}
              >
                <Edit2 size={12} strokeWidth={2} /> Edit
              </button>

              <button
                className="jdp-delete-btn"
                onClick={() => setConfirmDelete(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  height: 30,
                  padding: "0 10px",
                  borderRadius: T.radius - 1,
                  border: "0.5px solid transparent",
                  background: "transparent",
                  color: "#ef4444",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: MONO,
                  letterSpacing: "0.04em",
                  outline: "none",
                  transition: "background 0.12s",
                }}
              >
                <Trash2 size={12} strokeWidth={2} /> Delete
              </button>

              <div
                style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 2px" }}
              />

              <button
                className="jdp-icon-btn"
                onClick={onClose}
                style={{
                  width: 30,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: T.radius - 1,
                  border: T.border,
                  background: T.bg,
                  color: T.textTertiary,
                  cursor: "pointer",
                  outline: "none",
                  transition: "all 0.12s",
                  flexShrink: 0,
                }}
              >
                <X size={13} strokeWidth={2} />
              </button>
            </div>
          </div>

          {/* ── BODY ──────────────────────────────────────────────────────── */}
          <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

            {/* LEFT — main content */}
            <div
              className="jdp-left"
              style={{ flex: 1, overflowY: "auto", background: "#fff" }}
            >
              {/* About the role */}
              {listing?.requirements && (
                <div style={{ padding: "24px 24px 0" }}>
                  <SectionLabel>About the role</SectionLabel>
                  <JdBody html={listing.requirements} />
                </div>
              )}

              {/* Requirements */}
              {(exp || notice) && (
                <>
                  <div style={{ height: 1, background: "#e5e7eb", margin: "20px 0" }} />
                  <div style={{ padding: "0 24px" }}>
                    <SectionLabel>Requirements</SectionLabel>
                    <div style={{ display: "flex", gap: 32 }}>
                      {exp && (
                        <div>
                          <p
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: T.textTertiary,
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              margin: "0 0 6px",
                              fontFamily: MONO,
                            }}
                          >
                            Min. experience
                          </p>
                          <p
                            style={{
                              fontSize: 28,
                              fontWeight: 700,
                              color: T.text,
                              margin: 0,
                              lineHeight: 1,
                              letterSpacing: "-0.02em",
                              fontFamily: FF,
                            }}
                          >
                            {exp}
                            <span
                              style={{
                                fontSize: 13,
                                color: T.textTertiary,
                                fontWeight: 500,
                                marginLeft: 4,
                              }}
                            >
                              yrs
                            </span>
                          </p>
                          {expDB && (
                            <p
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#ef4444",
                                margin: "5px 0 0",
                                fontFamily: MONO,
                                letterSpacing: "0.04em",
                              }}
                            >
                              Dealbreaker
                            </p>
                          )}
                        </div>
                      )}
                      {notice && (
                        <div>
                          <p
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: T.textTertiary,
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              margin: "0 0 6px",
                              fontFamily: MONO,
                            }}
                          >
                            Max. notice period
                          </p>
                          <p
                            style={{
                              fontSize: 28,
                              fontWeight: 700,
                              color: T.text,
                              margin: 0,
                              lineHeight: 1,
                              letterSpacing: "-0.02em",
                              fontFamily: FF,
                            }}
                          >
                            {notice}
                          </p>
                          {noticeDB && (
                            <p
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#ef4444",
                                margin: "5px 0 0",
                                fontFamily: MONO,
                                letterSpacing: "0.04em",
                              }}
                            >
                              Dealbreaker
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Preferred candidate */}
              {ideal.length > 0 && (
                <>
                  <div style={{ height: 1, background: "#e5e7eb", margin: "20px 0" }} />
                  <div style={{ padding: "0 24px" }}>
                    <SectionLabel>Preferred candidate profile</SectionLabel>
                    <ul
                      style={{
                        listStyle: "none",
                        margin: 0,
                        padding: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {ideal.map((item, i) => (
                        <li
                          key={i}
                          style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                        >
                          <span
                            style={{
                              marginTop: 8,
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              background: "#d1d5db",
                              flexShrink: 0,
                              display: "block",
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              flex: 1,
                              alignItems: "baseline",
                              justifyContent: "space-between",
                              gap: 12,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13.5,
                                color: T.textMuted,
                                lineHeight: 1.65,
                                fontFamily: FF,
                              }}
                            >
                              {item.requirement}
                            </span>
                            {item.weightage != null && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: T.textTertiary,
                                  flexShrink: 0,
                                  fontFamily: MONO,
                                }}
                              >
                                {item.weightage}%
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {/* Red flags */}
              {flags.length > 0 && (
                <>
                  <div style={{ height: 1, background: "#e5e7eb", margin: "20px 0" }} />
                  <div style={{ padding: "0 24px" }}>
                    <SectionLabel>Red flags</SectionLabel>
                    <ul
                      style={{
                        listStyle: "none",
                        margin: 0,
                        padding: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {flags.map((f, i) => (
                        <li
                          key={i}
                          style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                        >
                          <span
                            style={{
                              marginTop: 8,
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              background: "#fca5a5",
                              flexShrink: 0,
                              display: "block",
                            }}
                          />
                          <span
                            style={{
                              fontSize: 13.5,
                              color: T.textMuted,
                              lineHeight: 1.65,
                              fontFamily: FF,
                            }}
                          >
                            {f}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {/* Application questions */}
              {qs.length > 0 && (
                <>
                  <div style={{ height: 1, background: "#e5e7eb", margin: "20px 0" }} />
                  <div style={{ padding: "0 24px" }}>
                    <SectionLabel>Application questions</SectionLabel>
                    <p
                      style={{
                        fontSize: 12.5,
                        color: T.textTertiary,
                        margin: "0 0 16px",
                        lineHeight: 1.55,
                        fontFamily: FF,
                      }}
                    >
                      These custom questions are added to the job posting after the default questions.
                    </p>

                    <button
                      className="jdp-ghost-btn"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        height: 30,
                        padding: "0 12px",
                        borderRadius: T.radius - 1,
                        marginBottom: 16,
                        fontSize: 12,
                        fontWeight: 700,
                        color: T.textMuted,
                        background: T.bg,
                        border: T.border,
                        cursor: "pointer",
                        fontFamily: MONO,
                        letterSpacing: "0.04em",
                        outline: "none",
                        transition: "background 0.12s",
                      }}
                    >
                      Add custom questions <ChevronDown size={11} strokeWidth={2} />
                    </button>

                    <div
                      style={{
                        border: T.border,
                        borderRadius: T.radiusCard,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 14px",
                          borderBottom: T.border,
                          background: T.bgMuted,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: T.text,
                            fontFamily: MONO,
                            letterSpacing: "0.04em",
                          }}
                        >
                          {listing.role}
                        </span>
                        <button
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#d1d5db",
                            display: "flex",
                          }}
                        >
                          <Trash2 size={13} strokeWidth={1.75} />
                        </button>
                      </div>

                      {qs.map((q, i) => (
                        <div
                          key={q.id || i}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 20,
                            padding: "16px 14px",
                            borderBottom: i !== qs.length - 1 ? T.border : "none",
                            alignItems: "flex-start",
                          }}
                        >
                          <div
                            style={{ display: "flex", alignItems: "flex-start", gap: 4 }}
                          >
                            <span
                              style={{
                                fontSize: 13.5,
                                color: T.textMuted,
                                lineHeight: 1.55,
                                fontFamily: FF,
                              }}
                            >
                              {q.text}
                            </span>
                            {q.required && (
                              <span
                                style={{
                                  color: "#ef4444",
                                  fontSize: 14,
                                  marginTop: 1,
                                  flexShrink: 0,
                                }}
                              >
                                ✻
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              background: T.bgMuted,
                              border: T.border,
                              borderRadius: T.radius - 1,
                              minHeight: 80,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Interview rounds */}
              {rounds.length > 0 && (
                <>
                  <div style={{ height: 1, background: "#e5e7eb", margin: "20px 0" }} />
                  <div style={{ padding: "0 24px 24px" }}>
                    <SectionLabel>Interview rounds</SectionLabel>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {rounds.map((r, i) => (
                        <div
                          key={i}
                          className="jdp-round-row"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "9px 10px",
                            borderRadius: T.radius - 1,
                            transition: "background 0.1s",
                            cursor: "default",
                            background: "transparent",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#d1d5db",
                                width: 20,
                                textAlign: "right",
                                fontFamily: MONO,
                              }}
                            >
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span
                              style={{
                                fontSize: 13.5,
                                fontWeight: 500,
                                color: T.text,
                                fontFamily: FF,
                              }}
                            >
                              {r.roundName || `Round ${i + 1}`}
                            </span>
                          </div>

                          {r.roundType && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "2px 8px",
                                borderRadius: 99,
                                fontSize: 11,
                                fontWeight: 700,
                                fontFamily: MONO,
                                letterSpacing: "0.03em",
                                ...(r.roundType === "Ai"
                                  ? { background: "#f5f3ff", color: "#7c3aed", border: "0.5px solid #ddd6fe" }
                                  : r.roundType === "Human+Ai"
                                  ? { background: "#eff6ff", color: "#2563eb", border: "0.5px solid #bfdbfe" }
                                  : { background: "#f3f4f6", color: "#6b7280", border: "0.5px solid #e5e7eb" }),
                              }}
                            >
                              {(r.roundType === "Ai" || r.roundType === "Human+Ai") && (
                                <Zap size={9} strokeWidth={2} />
                              )}
                              {r.roundType}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div style={{ height: 48 }} />
            </div>

            {/* RIGHT — sidebar */}
            <div
              className="jdp-right"
              style={{
                width: 224,
                flexShrink: 0,
                overflowY: "auto",
                background: T.bgPage,
                borderLeft: T.border,
                padding: "20px 18px 28px",
              }}
            >
              {/* Details */}
              <div style={{ marginBottom: 0 }}>
                <SbKey>Details</SbKey>
                <div style={{ display: "flex", flexDirection: "column", gap: 11, marginTop: 10 }}>
                  {listing?.location && (
                    <div>
                      <SbKey>Location</SbKey>
                      <SbVal>{listing.location}</SbVal>
                    </div>
                  )}
                  {listing?.workMode && (
                    <div>
                      <SbKey>Work mode</SbKey>
                      <SbVal>{listing.workMode}</SbVal>
                    </div>
                  )}
                  {listing?.department && (
                    <div>
                      <SbKey>Department</SbKey>
                      <SbVal>{listing.department}</SbVal>
                    </div>
                  )}
                  {salary && (
                    <div>
                      <SbKey>Salary</SbKey>
                      <SbVal>{salary} / yr</SbVal>
                    </div>
                  )}
                  {listing?.createdAt && (
                    <div>
                      <SbKey>Posted</SbKey>
                      <SbVal>
                        {new Date(listing.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </SbVal>
                    </div>
                  )}
                </div>
              </div>

              <SbDivider />

              {/* Hiring manager */}
              <div style={{ marginBottom: 0 }}>
                <SbKey style={{ marginBottom: 10 }}>Hiring manager</SbKey>
                <div style={{ marginTop: 10 }}>
                  {organization?.hiringManager ? (
                    <PersonRow name={organization.hiringManager} />
                  ) : (
                    <p
                      style={{
                        fontSize: 13,
                        color: T.textTertiary,
                        margin: 0,
                        fontStyle: "italic",
                        fontFamily: FF,
                      }}
                    >
                      Not assigned
                    </p>
                  )}
                </div>
              </div>

              <SbDivider />

              {/* Access */}
              <div style={{ marginBottom: 0 }}>
                <SbKey>Access</SbKey>
                <div style={{ marginTop: 10 }}>
                  <PersonRow name="Admin" sub="Super Admin" />
                  {organization?.hiringManager && (
                    <PersonRow name={organization.hiringManager} />
                  )}
                  {(organization?.accessMembers ?? []).map((m, i) => (
                    <PersonRow key={i} name={m} />
                  ))}
                </div>
              </div>

              {/* Requisition */}
              {listing?.jobId && (
                <>
                  <SbDivider />
                  <div>
                    <SbKey>Requisition</SbKey>
                    <div style={{ marginTop: 8 }}>
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: T.text,
                          margin: "0 0 2px",
                          fontFamily: MONO,
                          letterSpacing: "0.02em",
                        }}
                      >
                        #{listing.jobId}
                      </p>
                      {listing?.department && (
                        <p style={{ fontSize: 12.5, color: T.textTertiary, margin: 0, fontFamily: FF }}>
                          {listing.department}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              <SbDivider />

              {/* Applicants */}
              <div>
                <SbKey>Applicants</SbKey>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <Users size={13} strokeWidth={1.5} color={T.textTertiary} />
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: T.text,
                      lineHeight: 1,
                      letterSpacing: "-0.02em",
                      fontFamily: FF,
                    }}
                  >
                    {listing?.applicantCount ?? 0}
                  </span>
                  <button
                    onClick={() =>
                      navigate?.(`/jobs/${listing?.organizationId}/${listing?.id}`)
                    }
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#2563eb",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      marginLeft: 2,
                      fontFamily: MONO,
                      letterSpacing: "0.04em",
                      outline: "none",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#1d4ed8")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#2563eb")}
                  >
                    View →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default JobDetailsPanel